import type { AdditionalDetailsData } from '@adyen/adyen-web';
import {
	type ActorRefFrom,
	assign,
	fromPromise,
	sendParent,
	sendTo,
	setup,
	type StateFrom,
} from 'xstate';

import { useSetSearchParams } from 'hooks/navigation';
import type { Cart, PaymentOption, PaymentResult } from 'models/api';
import { makeImplementationWarning } from 'state-machines/helpers';
import type { CartId } from 'types';
import {
	isPaymentTypeWithComponentState,
	type PaymentComponentState,
	type PaymentComponentWithStatePaymentType,
} from 'utils/business-logic';
import { browserRedirect } from 'utils/helpers';

import { isPaymentReadyForCheckout } from '../helpers';

import { finalizePayment, requestPayment } from './payment.actors';
import type {
	PaymentInput,
	PaymentMachineContext,
	PaymentMachineEvents,
} from './payment.types';

export const paymentMachine = setup({
	// oxlint-disable-next-line typescript/no-unnecessary-type-assertion
	types: {} as {
		context: PaymentMachineContext;
		events: PaymentMachineEvents;
		input: PaymentInput;
	},
	guards: {
		isValidPaymentState: ({ context }) =>
			isPaymentReadyForCheckout(
				context.selectedPayment?.selectedPaymentType,
				context.paymentComponentState,
			),
		requiresAdditionalAction: (
			_,
			params: { paymentResult: PaymentResult | undefined },
		) =>
			Boolean(
				params.paymentResult?.resultCode === 'Pending' &&
				params.paymentResult.action?.type === 'await',
			),
		paymentPending: (_, params: { paymentResult: PaymentResult | undefined }) =>
			params.paymentResult?.resultCode === 'Pending',
		redirectShopper: (
			_,
			params: { paymentResult: PaymentResult | undefined },
		) => params.paymentResult?.resultCode === 'RedirectShopper',
	},
	actions: {
		updateData: assign({
			cart: (_, params: { cart: Cart | undefined }) => params.cart,
			availablePaymentMethods: (_, params) =>
				params.cart?.availablePaymentMethods,
			selectedPayment: (_, params) => params.cart?.selectedPayment,
			errors: (_, params) => params.cart?.errorList,
		}),

		resetToOldPayment: assign({
			selectedPayment: ({ context }) => context.oldSelectedPayment,
		}),

		notifyParentPaymentDone: sendTo(
			({ context }) => context.checkoutMachineRef,
			(_, params: { orderData: Cart | undefined }) => {
				if (!params.orderData) {
					throw new Error('Order data is missing');
				}
				return {
					type: 'PAYMENT_DONE',
					orderData: params.orderData,
				};
			},
		),

		notifyParentPaymentFailed: sendTo(
			({ context }) => context.checkoutMachineRef,
			{
				type: 'PAYMENT_FAILED',
			},
		),
		sendPaymentError: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => {
				if (
					context.selectedPayment &&
					isPaymentTypeWithComponentState(
						context.selectedPayment.selectedPaymentType,
					) &&
					!context.paymentComponentState?.isValid
				) {
					const paymentKeys = {
						scheme: 'CardPaymentInfoError',
						blik: 'BlikPaymentInfoError',
					} as const satisfies Record<
						PaymentComponentWithStatePaymentType,
						string
					>;
					return {
						type: 'PAYMENT_STATE_ERROR',
						error: paymentKeys[context.selectedPayment.selectedPaymentType],
					};
				}
				return {
					type: 'PAYMENT_STATE_ERROR',
					error: 'GeneralPaymentInfoError' as const,
				};
			},
		),
		sendPaymentOk: sendTo(({ context }) => context.checkoutMachineRef, {
			type: 'PAYMENT_STATE_OK',
		}),

		setPaymentComponentState: assign({
			paymentComponentState: (_, params: { state: PaymentComponentState }) =>
				params.state,
		}),
		resetPaymentComponentState: assign({
			paymentComponentState: () => ({ isValid: false }),
		}),

		addCartIdToUrl: makeImplementationWarning('addCartIdToUrl'),

		setResult: assign({
			paymentResult: (
				_,
				params: { paymentResult: PaymentResult | undefined },
			) => params.paymentResult,
		}),

		checkCartCheckoutStatus: sendTo(
			({ context }) => context.checkoutMachineRef,
			{
				type: 'CHECK_CHECKOUT_STATUS',
			},
		),

		verifyPayment: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => ({
				type: 'receiveAdditionalDetails',
				data: context.additionalDetailsData,
			}),
		),

		saveSelectedPaymentToContext: assign({
			selectedPayment: (_, params: { selectedPayment: PaymentOption }) =>
				params?.selectedPayment,
		}),

		setAdditionalDetailsData: assign({
			additionalDetailsData: (
				_,
				params: { additionalDetailsData: AdditionalDetailsData },
			) => params.additionalDetailsData,
		}),

		sendPaymentComponentStateToParent: sendParent(({ context }) => ({
			type: 'SET_PAYMENT_COMPONENT_STATE',
			state: context.paymentComponentState,
		})),

		sendSelectedPaymentToParent: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					selectedPayment: context.selectedPayment,
				},
			}),
		),
		redirectToPaymentProvider: (
			_,
			params: { paymentResult: PaymentResult | undefined },
		) => {
			const action = params.paymentResult?.action;
			if (action?.url) {
				browserRedirect({
					url: action.url,
					method: action.method,
					data: action.data,
				});
			}
		},
	},
	actors: {
		requestPayment: fromPromise(
			({ input: { context } }: { input: { context: PaymentMachineContext } }) =>
				requestPayment(context),
		),
		finalizePayment: fromPromise(
			({ input: { cartId } }: { input: { cartId: CartId | undefined } }) =>
				finalizePayment(cartId),
		),
	},
}).createMachine({
	id: 'payment',
	initial: 'idle',

	context: ({ input: { checkoutMachineRef } }) => ({
		paymentErrorTypes: ['PaymentMethodNotNeeded'],
		additionalDetailsData: undefined,
		paymentResult: {
			orderNo: '',
			resultCode: '',
		},
		paymentComponentState: {},
		redirectResult: '',
		checkoutMachineRef,
	}),
	states: {
		idle: {
			id: 'idle',
			on: {
				CART_UPDATED: [
					{
						target: '#idle',
						actions: {
							type: 'updateData',
							params: ({ event }) => ({ cart: event.value }),
						},
					},
				],
				SELECT_PAYMENT_METHOD: {
					target: 'updatePaymentMethod',
					actions: [
						'resetPaymentComponentState',

						{
							type: 'saveSelectedPaymentToContext',
							params: ({ event }) => ({
								selectedPayment: event.value,
							}),
						},
					],
				},
				SET_PAYMENT_COMPONENT_STATE: {
					target: '#idle',
					actions: [
						{
							type: 'setPaymentComponentState',
							params: ({ event }) => ({
								state: event.state,
							}),
						},

						'sendPaymentComponentStateToParent',
					],
				},
			},
		},
		updatePaymentMethod: {
			initial: 'debouncePaymentMethodUpdate',
			states: {
				debouncePaymentMethodUpdate: {
					id: 'debouncePaymentMethodUpdate',
					after: {
						250: {
							target: 'loading',
							actions: 'sendSelectedPaymentToParent',
						},
					},
				},
				loading: {
					on: {
						CART_UPDATED: {
							target: '#idle',
							actions: {
								type: 'updateData',
								params: ({ event }) => ({ cart: event.value }),
							},
						},
					},
				},
			},
			on: {
				SELECT_PAYMENT_METHOD: {
					target: '#debouncePaymentMethodUpdate',
					reenter: true,
					actions: [
						'resetPaymentComponentState',
						{
							type: 'saveSelectedPaymentToContext',
							params: ({ event }) => ({
								selectedPayment: event.value,
							}),
						},
					],
				},
			},
		},

		paymentInitiating: {
			id: 'paymentInitiating',
			invoke: {
				src: 'requestPayment',
				input: ({ context }) => ({ context }),
				onDone: [
					{
						target: '#idle',
						guard: {
							type: 'requiresAdditionalAction',
							params: ({ event }) => ({
								paymentResult: event.output,
							}),
						},
						actions: [
							{
								type: 'setResult',
								params: ({ event }) => ({
									paymentResult: event.output,
								}),
							},
							'checkCartCheckoutStatus',
						],
					},
					{
						target: 'waitingForVerifyPaymentEvent',
						guard: {
							type: 'paymentPending',
							params: ({ event }) => ({
								paymentResult: event.output,
							}),
						},
						actions: [
							{
								type: 'setResult',
								params: ({ event }) => ({
									paymentResult: event.output,
								}),
							},
						],
					},
					{
						target: 'waitingForVerifyPaymentEvent',
						guard: {
							type: 'redirectShopper',
							params: ({ event }) => ({
								paymentResult: event.output,
							}),
						},
						actions: [
							{
								type: 'redirectToPaymentProvider',
								params: ({ event }) => ({
									paymentResult: event.output,
								}),
							},
							'addCartIdToUrl',
						],
					},
					{
						target: '#idle',
						actions: ['checkCartCheckoutStatus'],
					},
				],
				onError: {
					target: '#idle',
					actions: ['notifyParentPaymentFailed'],
				},
			},
		},
		waitingForVerifyPaymentEvent: {
			on: {
				receiveAdditionalDetails: {
					target: '#idle',
					actions: [
						{
							type: 'setAdditionalDetailsData',
							params: ({ event }) => ({
								additionalDetailsData: event.data,
							}),
						},
						'verifyPayment',
					],
				},
			},
		},
		paymentFinalizing: {
			id: 'paymentFinalizing',
			invoke: {
				src: 'finalizePayment',
				input: ({ context }) => ({
					cartId: context.cart?.id,
				}),
				onDone: {
					target: '#idle',
					actions: {
						type: 'notifyParentPaymentDone',
						params: ({ event }) => ({ orderData: event.output }),
					},
				},
				onError: {
					target: 'error',
				},
			},
		},
		error: {},
	},
	on: {
		FINALIZE_PAYMENT: {
			target: '#paymentFinalizing',
		},
		CHECK_PAYMENT_STATE: [
			{
				target: '#idle',
				guard: 'isValidPaymentState',
				actions: 'sendPaymentOk',
			},
			{
				target: '#idle',
				actions: 'sendPaymentError',
			},
		],
		INIT_PAYMENT: {
			target: '#paymentInitiating',
		},
		CART_UPDATED: {
			actions: {
				type: 'updateData',
				params: ({ event }) => ({ cart: event.value }),
			},
		},
		RESET_TO_OLD: {
			target: '#idle',
			actions: 'resetToOldPayment',
		},
	},
});
export type PaymentMachineState = StateFrom<typeof paymentMachine>;
export type PaymentMachineActor = ActorRefFrom<typeof paymentMachine>;

export function usePaymentMachine() {
	const { mergeSearchParams } = useSetSearchParams();

	return paymentMachine.provide({
		actions: {
			addCartIdToUrl: ({ context }) => {
				if (!context.cart) {
					return;
				}
				mergeSearchParams({ cartId: context.cart.id });
			},
		},
	});
}
